import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Warehouse, Edit2, Check, AlertTriangle, Package, Search, ChevronLeft, ChevronRight, Download, ArrowUp, ArrowDown, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { productAPI , authAPI } from '../../api';
import './Inventory.css';
import AccessModule from "../../components/AccessModule/AccessModule";
import Swal from 'sweetalert2';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function InventoryPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editStock, setEditStock] = useState('');
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [overallTotal, setOverallTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [copiedSku, setCopiedSku] = useState(null);
  const [overallLowStocks, setOverallLowStocks] = useState(0);
  const [overallOutOfStocks, setOverallOutOfStocks] = useState(0);
  const location = useLocation();
  const [search, setSearch] = useState(location.state?.search || '');
  const [status, setStatus] = useState('all');
  const [sortBy, setSortBy] = useState('_id');
  const [sortOrder, setSortOrder] = useState('desc');
   const userDetails = JSON.parse(localStorage.getItem("seller") || "{}" );

  const startEdit = (p: any) => { setEditingId(p._id); setEditStock(String(p.stock)); };
  const cancelEdit = () => { setEditingId(null); setEditStock(''); };

  const handleCardClick = (targetStatus: string) => {
    if (targetStatus === 'all') {
      setStatus('all');
    } else {
      setStatus(prev => (prev === targetStatus ? 'all' : targetStatus));
    }
    setPage(1);
  };

  const [accessChecked, setAccessChecked] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);  
  
    useEffect(() => {
    const checkAccess = async () => {
      try {
        const userDetails = JSON.parse(
          localStorage.getItem("seller") || "{}"
        );
  
        const sellerRes = await authAPI.getSellerStatus(
          userDetails._id
        );
  
        setVerificationStatus(
          sellerRes.data.verification_status
        );
  
        setIsBlocked(
          sellerRes.data.isBlock
        );
  
        setAccessChecked(true);
      } catch (err) {
        console.error(err);
        setAccessChecked(true);
      }
    };
  
    checkAccess();
  }, []);
  
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (!accessChecked) return;

    if (
      isBlocked ||
      verificationStatus === "P" ||
      verificationStatus === "R"
    ) {
      return;
    }

    fetchInventory();
  }, [
    page,
    limit,
    debouncedSearch,
    status,
    sortBy,
    sortOrder,
    accessChecked,
    isBlocked,
    verificationStatus
  ]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };


  const saveStock = async (productId: string) => {
    setSaving(true);
    try {
      const params = {
        productId: productId, 
        stock: Number(editStock)
      }
      await productAPI.bulkUpdateStock(params);
      setProducts((prev: any) => prev.map((p: any) => p._id === productId ? { ...p, stock: Number(editStock) } : p));
      toast.success('Stock updated!');
      cancelEdit();
      fetchInventory();
    } catch {
      setProducts((prev: any) => prev.map((p: any) => p._id === productId ? { ...p, stock: Number(editStock) } : p));
      toast.success('Stock updated (Dummy)');
      cancelEdit();
    }
    finally { setSaving(false); }
  };


const fetchInventory = async () => {
  setLoading(true);

  try {
    const res = await productAPI.getInventoryList({
      page,
      limit,
      search: debouncedSearch,
      status,
      seller_id : userDetails._id,
      sortBy,
      sortOrder
    });

    if (res.data?.response === "S") {
      setProducts(res.data.data || []);

      const pag = res.data.pagination || {};
      const summary = res.data.summary || {};

      setTotal(pag.total || 0);
      setPages(pag.pages || 1);

      if (summary.total !== undefined) {
        setOverallTotal(summary.total);
      } else if (status === 'all') {
        setOverallTotal(pag.total || 0);
      }

      setOverallLowStocks(summary.low_stock || 0);
      setOverallOutOfStocks(summary.out_of_stock || 0);
    }
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};

  const getPaginationGroup = () => {
    const pagesList: (number | string)[] = [];
    const maxPages = Number(pages) || 1;
    const currentPage = Number(page) || 1;

    if (maxPages <= 8) {
      for (let i = 1; i <= maxPages; i++) pagesList.push(i);
    } else {
      const lb = 2, rb = 2;
      let sp = Math.max(lb + 1, currentPage - 1);
      let ep = Math.min(maxPages - rb, currentPage + 1);

      if (currentPage <= lb + 2) ep = lb + 3;
      if (currentPage >= maxPages - rb - 1) sp = maxPages - rb - 2;

      for (let i = 1; i <= lb; i++) pagesList.push(i);
      if (sp > lb + 1) pagesList.push("...");
      for (let i = sp; i <= ep; i++) pagesList.push(i);
      if (ep < maxPages - rb) pagesList.push("...");
      for (let i = maxPages - rb + 1; i <= maxPages; i++) pagesList.push(i);
    }
    return pagesList;
  };

  const [exporting, setExporting] = useState(false);

  const fetchExportData = async () => {
    const res = await productAPI.getInventoryList({
      page: 1,
      limit: 10000,
      search: debouncedSearch,
      status,
      seller_id: userDetails._id
    });
    if (res.data?.response === "S") {
      return res.data.data || [];
    }
    return [];
  };

  const exportToPDF = async () => {
    try {
      setExporting(true);
      const dataToExport = await fetchExportData();

      if (dataToExport.length === 0) {
        Swal.fire({ title: 'No Data', text: 'There is no data to export.', icon: 'info', confirmButtonColor: '#14b8a6' });
        return;
      }

      const doc = new jsPDF();

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(20, 184, 166);
      doc.text("DECSOPLUS STORE", 14, 15);

      doc.setFontSize(10);
      doc.setFont("Helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text("Inventory Report", 14, 21);

      const dateStr = new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
      doc.text(`Generated: ${dateStr}`, 196, 15, { align: 'right' });

      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.line(14, 25, 196, 25);

      doc.setFontSize(9);
      doc.setFont("Helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.text(`Total Products: ${dataToExport.length}`, 14, 31);

      const tableHeaders = [["S.No", "Product Name", "SKU", "Category", "Stock", "Alert At"]];
      const tableRows = dataToExport.map((p: any, index: number) => [
        (index + 1).toString(),
        p.name || '—',
        p.sku || '—',
        p.category || '—',
        p.stock?.toString() || '0',
        p.lowStockAlert?.toString() || '0'
      ]);

      autoTable(doc, {
        startY: 35,
        head: tableHeaders,
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [20, 184, 166], textColor: 255, fontSize: 9, fontStyle: 'bold', halign: 'left' },
        bodyStyles: { fontSize: 8, textColor: [51, 65, 85], valign: 'middle' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didDrawPage: (data: any) => {
          const pageCount = doc.getNumberOfPages();
          doc.setFontSize(8);
          doc.setFont("Helvetica", "normal");
          doc.setTextColor(148, 163, 184);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
          doc.text("DecsoPlus © All Rights Reserved", doc.internal.pageSize.width - data.settings.margin.right, doc.internal.pageSize.height - 10, { align: 'right' });
        },
        margin: { top: 35, bottom: 15, left: 14, right: 14 }
      });

      doc.save(`DecsoPlus_Inventory_${new Date().toISOString().slice(0, 10)}.pdf`);
      Swal.fire({ title: 'Success!', text: 'Inventory exported to PDF successfully.', icon: 'success', confirmButtonColor: '#14b8a6' });
    } catch (err: any) {
      Swal.fire({ title: 'Export Failed', text: err.message || 'Something went wrong.', icon: 'error', confirmButtonColor: '#14b8a6' });
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = async () => {
    try {
      setExporting(true);
      const dataToExport = await fetchExportData();

      if (dataToExport.length === 0) {
        Swal.fire({ title: 'No Data', text: 'There is no data to export.', icon: 'info', confirmButtonColor: '#14b8a6' });
        return;
      }

      const headers = ["S.No", "Product Name", "SKU", "Category", "Stock", "Alert At"];
      const rows = dataToExport.map((p: any, index: number) => [
        index + 1,
        `"${(p.name || '').replace(/"/g, '""')}"`,
        `"${(p.sku || '').replace(/"/g, '""')}"`,
        `"${(p.category || '').replace(/"/g, '""')}"`,
        p.stock || 0,
        p.lowStockAlert || 0
      ]);

      const csvContent = [headers.join(","), ...rows.map((r: any) => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `DecsoPlus_Inventory_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Swal.fire({ title: 'Success!', text: 'Inventory exported to CSV successfully.', icon: 'success', confirmButtonColor: '#14b8a6' });
    } catch (err: any) {
      Swal.fire({ title: 'Export Failed', text: err.message || 'Something went wrong.', icon: 'error', confirmButtonColor: '#14b8a6' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportClick = () => {
    Swal.fire({
      title: 'Export Format',
      text: 'Choose which format you want to export to:',
      icon: 'question',
      showCloseButton: true,
      showCancelButton: false,
      showDenyButton: true,
      confirmButtonText: 'Export as CSV',
      denyButtonText: 'Export as PDF',
      confirmButtonColor: '#10b981',
      denyButtonColor: '#f97316',
      width: '400px',
      customClass: {
        actions: 'my-actions',
        confirmButton: 'order-1',
        denyButton: 'order-2',
      }
    }).then((result) => {
      if (result.isConfirmed) {
        exportToCSV();
      } else if (result.isDenied) {
        exportToPDF();
      }
    });
  };

 
    
    if (
      isBlocked ||
      verificationStatus === "P" ||
      verificationStatus === "R"
    ) {
      return (
        <AccessModule
          isBlock={isBlocked}
          verification_status={verificationStatus}
        />
      );
    }
    
  
  return (
    <div className="fade-in" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 className="page-title">Inventory</h1>
          <p className="page-subtitle">Manage your product stock levels</p>
        </div>
      </div>

      <div className="inventory-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
        {[
          { key: 'all', label: 'Total Products', value: overallTotal || total, color: '#14b8a6', bg: '#f0fdfa', activeBg: '#e6fffa', icon: Package },
          { key: 'low_stock', label: 'Low Stock', value: overallLowStocks, color: '#f59e0b', bg: '#fffbeb', activeBg: '#fef3c7', icon: AlertTriangle },
          { key: 'out_of_stock', label: 'Out of Stock', value: overallOutOfStocks, color: '#ef4444', bg: '#fef2f2', activeBg: '#fee2e2', icon: Warehouse },
        ].map(({ key, label, value, color, bg, activeBg, icon: Icon }) => {
          const isSelected = status === key;
          return (
            <div
              key={key}
              onClick={() => handleCardClick(key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCardClick(key); }}
              title={`Click to filter by ${label}`}
              style={{
                background: isSelected ? activeBg : bg,
                border: isSelected ? `2.5px solid ${color}` : `1px solid ${color}28`,
                padding: '24px',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                cursor: 'pointer',
                boxShadow: isSelected
                  ? `0 10px 25px -3px ${color}35, 0 4px 6px -2px ${color}20`
                  : '0 4px 6px -1px rgba(0, 0, 0, 0.02)',
                transform: isSelected ? 'translateY(-2px)' : 'none',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                userSelect: 'none',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 8px 16px -2px ${color}30`;
                  e.currentTarget.style.borderColor = `${color}66`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.borderColor = `${color}28`;
                }
              }}
            >
              <div style={{
                background: isSelected ? `${color}35` : `${color}22`,
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}>
                <Icon size={24} color={color} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '26px', fontWeight: '800', color, lineHeight: '1.2' }}>{value}</div>
                <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '600', marginTop: '4px' }}>{label}</div>
              </div>
              {isSelected && (
                <div style={{
                  padding: '4px 10px',
                  borderRadius: '20px',
                  background: color,
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: '700',
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  boxShadow: `0 2px 6px ${color}45`
                }}>
                  Active
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="products-card">
        {/* Card Header: Search */}
        <div className="products-card-header">
          <div style={{ display: 'flex', gap: '12px', flex: 1, minWidth: '300px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '12px', flex: 1, minWidth: '300px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: '350px', minWidth: '220px' }}>
                <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  className="form-input"
                  placeholder="Search products, SKUs..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value.trimStart()); setPage(1); }}
                  style={{ paddingLeft: '40px', background: 'white', borderRadius: '10px' }}
                />
              </div>
              {search && (
                <button
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '11px 16px', background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s',
                    color: '#64748b', fontWeight: '600'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#334155'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b'; }}
                >
                  Clear
                </button>
              )}
              {status !== 'all' && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 14px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  background: status === 'low_stock' ? '#fffbeb' : '#fef2f2',
                  color: status === 'low_stock' ? '#b45309' : '#b91c1c',
                  border: `1px solid ${status === 'low_stock' ? '#fde68a' : '#fecaca'}`,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                }}>
                  <span>Filtered: <strong>{status === 'low_stock' ? 'Low Stock' : 'Out of Stock'}</strong></span>
                  <button
                    onClick={() => { setStatus('all'); setPage(1); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '700',
                      fontSize: '14px',
                      color: 'inherit',
                      padding: '0 2px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="Clear filter and show all products"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            <button 
              onClick={handleExportClick} 
              disabled={exporting}
              style={{ 
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'white', border: '1px solid #e2e8f0', color: '#475569', 
                borderRadius: '10px', padding: '11px 16px', fontWeight: '600', cursor: exporting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
              onMouseEnter={(e) => { if(!exporting) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
              onMouseLeave={(e) => { if(!exporting) { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; } }}
            >
              <Download size={16} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>

        {/* Scrollable Table Body Container */}
        <div className="products-table-container">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' }}>
              <div style={{ width: '36px', height: '36px', border: '3px solid #f1f5f9', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#475569' }}>Fetching inventory...</span>
            </div>
          ) : products.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', width: '100%', background: '#fff', borderRadius: '12px' }}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px' }}>
                <path d="M53.3333 18.6667H29.3333L24 13.3333H10.6667C7.72 13.3333 5.33333 15.72 5.33333 18.6667V45.3333C5.33333 48.28 7.72 50.6667 10.6667 50.6667H53.3333C56.28 50.6667 58.6667 48.28 58.6667 45.3333V24C58.6667 21.0533 56.28 18.6667 53.3333 18.6667Z" fill="url(#inv-folder-grad)"/>
                <defs>
                  <linearGradient id="inv-folder-grad" x1="5.33333" y1="13.3333" x2="58.6667" y2="50.6667" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FCD34D"/>
                    <stop offset="1" stopColor="#F59E0B"/>
                  </linearGradient>
                </defs>
              </svg>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px', fontFamily: 'inherit' }}>
                {status === 'low_stock' ? 'No low stock products found' : status === 'out_of_stock' ? 'No out of stock products found' : 'No inventory found'}
              </h3>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px', maxWidth: '400px', textAlign: 'center' }}>
                {status !== 'all' ? 'All stock levels are within normal range.' : 'No records match the selected category, dates, or search term.'}
              </p>
              {(search || status !== 'all') && (
                <button
                  onClick={() => { setSearch(''); setStatus('all'); setPage(1); }}
                  style={{ background: '#2563eb', color: '#ffffff', border: 'none', padding: '10px 24px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)' }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#1d4ed8'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#2563eb'}
                >
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <table style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th style={{ paddingLeft: '24px', width: '60px' }}>Sl.No</th>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Product {sortBy === 'name' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                    </div>
                  </th>
                  <th onClick={() => handleSort('sku')} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      SKU {sortBy === 'sku' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                    </div>
                  </th>
                  <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Category {sortBy === 'category' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                    </div>
                  </th>
                  <th onClick={() => handleSort('stock')} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Stock {sortBy === 'stock' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                    </div>
                  </th>
                  <th>Alert At</th>
                  <th style={{ paddingRight: '24px' }}>Update Stock</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p: any, index: number) => {
                  const isLow = p.stock <= p.lowStockAlert;
                  const isOut = p.stock === 0;
                  return (
                    <tr key={p._id} style={{ background: isOut ? '#fff5f5' : isLow ? '#fffbeb' : undefined }}>
                      <td style={{ paddingLeft: '24px', color: '#64748b', fontWeight: '600', fontSize: '14px' }}>
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          {p.images && p.images.length > 0 && (p.images[0]?.url || typeof p.images[0] === 'string') ? (
                            <img src={p.images[0]?.url || p.images[0]} alt={p.name} style={{ width: '44px', height: '44px', borderRadius: '10px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                          ) : (
                            <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                              <Package size={22} color="#94a3b8" />
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '14px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontFamily: 'monospace', background: '#f1f5f9', color: '#475569', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px' }}>{p.sku}</span>
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText(p.sku || '');
                              setCopiedSku(p.sku);
                              setTimeout(() => setCopiedSku(null), 2000);
                            }}
                            title="Copy SKU"
                            style={{ background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', padding: '4px 6px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: copiedSku === p.sku ? '#10b981' : '#64748b', transition: 'all 0.2s' }}
                            onMouseEnter={(e) => { if(copiedSku !== p.sku) { e.currentTarget.style.color = '#0f172a'; e.currentTarget.style.background = '#f1f5f9'; } }}
                            onMouseLeave={(e) => { if(copiedSku !== p.sku) { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = '#f8fafc'; } }}
                          >
                            {copiedSku === p.sku ? <Check size={13} /> : <Copy size={13} />}
                          </button>
                        </div>
                      </td>
                      <td style={{ color: '#64748b', fontSize: '13px', fontWeight: '500' }}>{p.category}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {isOut ? <span style={{ color: '#ef4444', fontWeight: '800', fontSize: '16px' }}>0</span> :
                            isLow ? <span style={{ color: '#f59e0b', fontWeight: '800', fontSize: '16px' }}>{p.stock}</span> :
                              <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '16px' }}>{p.stock}</span>
                          }
                        </div>
                      </td>
                      <td style={{ color: '#64748b', fontWeight: '500', fontSize: '13px' }}>{p.lowStockAlert}</td>
                      <td style={{ paddingRight: '24px' }}>
                        {editingId === p._id ? (
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <input
                              type="number"
                              value={editStock}
                              onChange={e => setEditStock(e.target.value)}
                              style={{ width: '70px', border: '1.5px solid #14b8a6', borderRadius: '8px', padding: '6px 8px', fontSize: '14px', fontWeight: '700', outline: 'none' }}
                              min="0"
                            />
                            <button onClick={() => saveStock(p._id)} disabled={saving}
                              style={{ background: '#14b8a6', border: 'none', borderRadius: '7px', padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}>
                              <Check size={14} color="white" />
                            </button>
                            <button onClick={cancelEdit}
                              style={{ background: '#f1f5f9', border: 'none', borderRadius: '7px', padding: '6px', cursor: 'pointer', fontSize: '13px', color: '#64748b', fontWeight: '600', transition: 'all 0.2s' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
                              >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => startEdit(p)}
                            style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '7px', padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#2563eb', transition: 'all 0.2s' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#dbeafe'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#eff6ff'}>
                            <Edit2 size={13} /> Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Card Footer: Pagination */}
        {!loading && products.length > 0 && (
          <div className="products-card-footer">
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                Showing <span style={{ fontWeight: '700', color: '#0f172a' }}>{total === 0 ? 0 : (page - 1) * limit + 1}</span> to <span style={{ fontWeight: '700', color: '#0f172a' }}>{Math.min(page * limit, total)}</span> of <span style={{ fontWeight: '700', color: '#0f172a' }}>{total}</span> products
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                Page size:
                <select 
                  value={limit} 
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} 
                  style={{ 
                    padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', 
                    color: '#334155', fontWeight: '600', cursor: 'pointer', outline: 'none'
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button 
                disabled={Number(page) <= 1}
                onClick={() => setPage(Number(page) - 1)}
                style={{ 
                  padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', 
                  cursor: Number(page) <= 1 ? 'not-allowed' : 'pointer', opacity: Number(page) <= 1 ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', color: '#475569', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { if (Number(page) > 1) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if (Number(page) > 1) e.currentTarget.style.background = 'white'; }}
                title="Previous page"
              >
                <ChevronLeft size={16} />
              </button>

              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {getPaginationGroup().map((pg, i) => {
                  if (pg === "...") {
                    return (
                      <span
                        key={`ellipsis-${i}`}
                        style={{
                          width: '30px',
                          height: '34px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94a3b8',
                          fontWeight: '700',
                          fontSize: '14px',
                          userSelect: 'none'
                        }}
                      >
                        …
                      </span>
                    );
                  }

                  const pageNum = Number(pg);
                  const isActive = Number(page) === pageNum;

                  return (
                    <button 
                      key={pageNum} 
                      onClick={() => setPage(pageNum)}
                      style={{
                        width: '34px', height: '34px', borderRadius: '8px', border: '1px solid',
                        borderColor: isActive ? '#14b8a6' : '#e2e8f0',
                        background: isActive ? '#14b8a6' : 'white',
                        color: isActive ? 'white' : '#475569',
                        fontWeight: isActive ? '700' : '600', cursor: 'pointer', fontSize: '13px',
                        transition: 'all 0.2s', boxShadow: isActive ? '0 4px 12px rgba(20,184,166,0.2)' : '0 1px 2px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                          e.currentTarget.style.background = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button 
                disabled={Number(page) >= (Number(pages) || 1)}
                onClick={() => setPage(Number(page) + 1)}
                style={{ 
                  padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', 
                  cursor: Number(page) >= (Number(pages) || 1) ? 'not-allowed' : 'pointer',
                  opacity: Number(page) >= (Number(pages) || 1) ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', color: '#475569', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { if (Number(page) < (Number(pages) || 1)) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if (Number(page) < (Number(pages) || 1)) e.currentTarget.style.background = 'white'; }}
                title="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
